import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { activeMembership, useSession } from '../../lib/store';
import { Card, Notice, PageHeading, Spinner } from '../../components/Shell';
import { QuoteTable } from '../../components/Money';
import { Stamp } from '../../components/Stamp';
import { TextArea, TextField } from '../../components/Field';
import { money, minutesToTime, rateSuffix } from '../../lib/format';
import type { AvailabilityDay, Blocker, BookingQuote, Pet, ServiceSummary } from '../../lib/types';

interface QuoteResponse {
  quote: BookingQuote;
  availability: { ok: boolean; conflicts: { date: string; reason: string }[] };
  blockers: Blocker[];
  bookable: boolean;
  confirmable: boolean;
}

/**
 * The booking flow. Everything on one page rather than a wizard, because the
 * price depends on the service, the pets and the dates together, and hiding
 * any one of them behind a step makes the total feel like a surprise.
 */
export function NewBooking() {
  const navigate = useNavigate();
  const session = useSession();
  const membership = activeMembership(session);
  const tenantId = session.tenant?.tenantId;

  const [pets, setPets] = useState<Pet[]>([]);
  const [services, setServices] = useState<ServiceSummary[]>([]);
  const [availability, setAvailability] = useState<AvailabilityDay[]>([]);
  const [loading, setLoading] = useState(true);

  const [serviceId, setServiceId] = useState('');
  const [selectedPets, setSelectedPets] = useState<Set<string>>(new Set());
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [visitCount, setVisitCount] = useState(1);
  const [addOns, setAddOns] = useState<Record<string, number>>({});
  const [clientNotes, setClientNotes] = useState('');

  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const service = useMemo(
    () => services.find((s) => s.serviceId === serviceId) ?? null,
    [services, serviceId]
  );

  useEffect(() => {
    if (!tenantId) return;
    const today = new Date().toISOString().slice(0, 10);
    const horizon = new Date();
    horizon.setDate(horizon.getDate() + 120);

    void Promise.all([
      api<{ pets: Pet[] }>(`/tenants/${tenantId}/pets`),
      api<{ services: ServiceSummary[] }>(`/tenants/${tenantId}/services`),
      api<{ days: AvailabilityDay[] }>(
        `/tenants/${tenantId}/availability?from=${today}&to=${horizon.toISOString().slice(0, 10)}`
      ),
    ])
      .then(([petResult, serviceResult, availabilityResult]) => {
        setPets(petResult.pets);
        setServices(serviceResult.services.filter((s) => s.active !== false));
        setAvailability(availabilityResult.days);
        if (petResult.pets.length === 1) setSelectedPets(new Set([petResult.pets[0].petId]));
        const boarding = serviceResult.services.find((s) => s.code === 'boarding');
        if (boarding) setServiceId(boarding.serviceId);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Booking could not be opened.'))
      .finally(() => setLoading(false));
  }, [tenantId]);

  /** Re-price whenever anything that affects the total changes. */
  const requestQuote = useCallback(() => {
    if (!tenantId || !serviceId || selectedPets.size === 0 || !startAt || !endAt) {
      setQuote(null);
      return;
    }

    setQuoting(true);
    setQuoteError(null);

    void api<QuoteResponse>(`/tenants/${tenantId}/bookings/quote`, {
      method: 'POST',
      body: {
        serviceId,
        petIds: [...selectedPets],
        startAt: new Date(startAt).toISOString(),
        endAt: new Date(endAt).toISOString(),
        visitCount: service?.rateType === 'per_visit' ? visitCount : undefined,
        selectedAddOns: Object.entries(addOns)
          .filter(([, quantity]) => quantity > 0)
          .map(([addOnId, quantity]) => ({ addOnId, quantity })),
      },
    })
      .then(setQuote)
      .catch((err) => {
        setQuote(null);
        setQuoteError(err instanceof ApiError ? err.message : 'The price could not be worked out.');
      })
      .finally(() => setQuoting(false));
  }, [tenantId, serviceId, selectedPets, startAt, endAt, visitCount, addOns, service?.rateType]);

  useEffect(() => {
    // Debounced so dragging a date picker does not fire a request per keystroke.
    const timer = setTimeout(requestQuote, 350);
    return () => clearTimeout(timer);
  }, [requestQuote]);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16">
        <Spinner label="Loading your options" />
      </div>
    );
  }

  if (pets.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-20">
        <PageHeading title="Add a pet first" lead="A booking needs at least one pet on file." />
        <button type="button" className="btn btn-primary" onClick={() => navigate('/portal/pets/new')}>
          Add a pet
        </button>
      </div>
    );
  }

  const blockedDates = availability.filter((d) => d.blocked || d.boarding.available === 0);

  const submit = () => {
    if (!tenantId || !quote?.bookable) return;
    setSubmitting(true);
    setError(null);

    void api<{ bookingId?: string; booking: { bookingId: string } }>(`/tenants/${tenantId}/bookings`, {
      method: 'POST',
      body: {
        serviceId,
        petIds: [...selectedPets],
        startAt: new Date(startAt).toISOString(),
        endAt: new Date(endAt).toISOString(),
        visitCount: service?.rateType === 'per_visit' ? visitCount : undefined,
        selectedAddOns: Object.entries(addOns)
          .filter(([, quantity]) => quantity > 0)
          .map(([addOnId, quantity]) => ({ addOnId, quantity })),
        clientNotes: clientNotes || undefined,
      },
    })
      .then((result) => navigate(`/portal/bookings/${result.booking.bookingId}`))
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : 'The booking did not go through.');
        setSubmitting(false);
      });
  };

  return (
    <div className="mx-auto max-w-5xl px-5 pb-24 pt-12">
      <PageHeading
        eyebrow={session.tenant?.businessName}
        title="Book a stay"
        lead="Pick a service, your pets and your dates. The full price shows as you go."
      />

      {error && (
        <div className="mb-8">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr]">
        <div className="space-y-6">
          {/* --- Service ---------------------------------------------------- */}
          <Card tab="Service" className="p-5">
            <div className="grid gap-3 sm:grid-cols-2">
              {services.map((option) => (
                <button
                  key={option.serviceId}
                  type="button"
                  onClick={() => {
                    setServiceId(option.serviceId);
                    setAddOns({});
                  }}
                  aria-pressed={serviceId === option.serviceId}
                  className={`rounded-sm border p-3 text-left transition-colors ${
                    serviceId === option.serviceId
                      ? 'border-[color:var(--color-stamp)] bg-[color:var(--color-stamp-soft)]'
                      : 'border-[color:var(--color-rule)] hover:border-[color:var(--color-ink)]'
                  }`}
                >
                  <span className="block font-display font-bold">{option.name}</span>
                  <span className="typed text-sm text-[color:var(--color-ink-soft)]">
                    {option.rateType === 'quote'
                      ? 'Quoted'
                      : option.basePrice === 0
                        ? 'Free'
                        : `${money(option.basePrice)} ${rateSuffix(option.rateType)}`}
                  </span>
                </button>
              ))}
            </div>

            {service?.windowStartMinutes !== undefined && service.windowEndMinutes !== undefined && (
              <p className="mt-4 font-mono text-xs text-[color:var(--color-ink-faint)]">
                Normal hours {minutesToTime(service.windowStartMinutes)} to{' '}
                {minutesToTime(service.windowEndMinutes)}. Outside those, an off-hours fee applies.
              </p>
            )}
          </Card>

          {/* --- Pets ------------------------------------------------------- */}
          <Card tab="Pets" className="p-5">
            <div className="space-y-2">
              {pets.map((pet) => {
                const checked = selectedPets.has(pet.petId);
                return (
                  <label
                    key={pet.petId}
                    className={`flex cursor-pointer items-center gap-3 rounded-sm border p-3 transition-colors ${
                      checked
                        ? 'border-[color:var(--color-stamp)] bg-[color:var(--color-stamp-soft)]'
                        : 'border-[color:var(--color-rule)]'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-[color:var(--color-stamp)]"
                      checked={checked}
                      onChange={(e) =>
                        setSelectedPets((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(pet.petId);
                          else next.delete(pet.petId);
                          return next;
                        })
                      }
                    />
                    <span className="flex-1">
                      <span className="font-display font-bold">{pet.name}</span>
                      {pet.breed && (
                        <span className="ml-2 typed text-sm text-[color:var(--color-ink-soft)]">{pet.breed}</span>
                      )}
                    </span>
                    {pet.medical.medicationRequired && (
                      <Stamp tone="pending" flat>
                        Medication
                      </Stamp>
                    )}
                  </label>
                );
              })}
            </div>
          </Card>

          {/* --- Dates ------------------------------------------------------ */}
          <Card tab="Dates" className="p-5">
            <div className="grid gap-6 sm:grid-cols-2">
              <TextField
                label={service?.rateType === 'per_visit' ? 'First visit' : 'Drop off'}
                type="datetime-local"
                value={startAt}
                onChange={setStartAt}
                required
              />
              <TextField
                label={service?.rateType === 'per_visit' ? 'Last visit' : 'Pick up'}
                type="datetime-local"
                value={endAt}
                onChange={setEndAt}
                required
              />
            </div>

            {service?.rateType === 'per_visit' && (
              <div className="mt-6">
                <TextField
                  label="How many visits"
                  type="number"
                  min={1}
                  max={200}
                  value={String(visitCount)}
                  onChange={(value) => setVisitCount(Math.max(1, Number(value) || 1))}
                  hint="Each visit is charged separately."
                />
              </div>
            )}

            {blockedDates.length > 0 && (
              <p className="mt-5 font-mono text-xs text-[color:var(--color-ink-faint)]">
                {blockedDates.length} date{blockedDates.length > 1 ? 's are' : ' is'} already full in the
                next four months. Your quote will say if yours is one of them.
              </p>
            )}
          </Card>

          {/* --- Add-ons ---------------------------------------------------- */}
          {service && service.addOns.filter((a) => a.active && a.addOnId !== 'medication').length > 0 && (
            <Card tab="Extras" tint="yellow" className="p-5">
              <div className="space-y-3">
                {service.addOns
                  .filter((a) => a.active && a.addOnId !== 'medication')
                  .map((addOn) => (
                    <div key={addOn.addOnId} className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold">{addOn.name}</p>
                        {addOn.description && (
                          <p className="text-xs text-[color:var(--color-ink-soft)]">{addOn.description}</p>
                        )}
                        <p className="typed text-xs text-[color:var(--color-ink-faint)]">
                          {money(addOn.price)} {addOn.unit.replace(/_/g, ' ')}
                        </p>
                      </div>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        aria-label={`Quantity for ${addOn.name}`}
                        value={addOns[addOn.addOnId] ?? 0}
                        onChange={(e) =>
                          setAddOns((prev) => ({
                            ...prev,
                            [addOn.addOnId]: Math.max(0, Number(e.target.value) || 0),
                          }))
                        }
                        className="w-20 border-b-2 border-[color:var(--color-rule)] bg-transparent px-1 py-1 text-center font-mono focus:border-[color:var(--color-stamp)] focus:outline-none"
                      />
                    </div>
                  ))}
              </div>
              <p className="mt-4 text-xs text-[color:var(--color-ink-soft)]">
                Medication is added automatically for any pet whose record says it is needed.
              </p>
            </Card>
          )}

          <Card tab="Anything else" className="p-5">
            <TextArea
              label="Notes for your sitter"
              value={clientNotes}
              onChange={setClientNotes}
              rows={3}
              placeholder="Flight lands late on the 4th, so pickup may be closer to 9pm."
            />
          </Card>
        </div>

        {/* --- Quote ------------------------------------------------------- */}
        <div className="lg:sticky lg:top-6 lg:self-start">
          <Card tab="Your quote" className="p-5">
            {quoting && <Spinner label="Working out the price" />}

            {!quoting && !quote && (
              <p className="py-6 text-sm text-[color:var(--color-ink-soft)]">
                {quoteError ?? 'Choose a service, your pets and your dates to see the price.'}
              </p>
            )}

            {!quoting && quote && (
              <>
                <QuoteTable quote={quote.quote} />

                {!quote.availability.ok && (
                  <div className="mt-5">
                    <Notice tone="error" title="Those dates are not available">
                      <ul className="mt-1 space-y-0.5">
                        {quote.availability.conflicts.slice(0, 5).map((conflict) => (
                          <li key={conflict.date} className="typed text-xs">
                            {conflict.date} is {conflict.reason === 'blocked' ? 'blocked off' : 'full'}
                          </li>
                        ))}
                      </ul>
                    </Notice>
                  </div>
                )}

                {quote.blockers.length > 0 && (
                  <div className="mt-5">
                    <Notice tone="warn" title="Still to do before this is confirmed">
                      <ul className="mt-1 space-y-1">
                        {quote.blockers.map((blocker) => (
                          <li key={`${blocker.code}-${blocker.petId ?? ''}`}>{blocker.message}</li>
                        ))}
                      </ul>
                    </Notice>
                    <p className="mt-2 text-xs text-[color:var(--color-ink-soft)]">
                      You can still request these dates now. They will be held while you finish up.
                    </p>
                  </div>
                )}

                <button
                  type="button"
                  className="btn btn-primary mt-6 w-full"
                  onClick={submit}
                  disabled={!quote.bookable || submitting}
                >
                  {submitting ? 'Requesting' : 'Request these dates'}
                </button>

                <p className="mt-3 text-center text-xs text-[color:var(--color-ink-soft)]">
                  {session.tenant?.businessName} confirms every booking before it is final.
                </p>
              </>
            )}
          </Card>

          {membership?.role !== 'client' && (
            <p className="mt-4 font-mono text-xs text-[color:var(--color-ink-faint)]">
              You are booking as staff. Pick the client from the admin screens instead.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
